/**
 * FinancialProfileTab — second tab of the consumer Profile page. Two-column
 * layout:
 *
 *   Left (1.5fr) — Inline pill sub-tabs (Employment / Assets / Liabilities)
 *                  + the existing EmploymentInfoForm / AssetsForm /
 *                  LiabilitiesForm components (reused — they already wire to
 *                  the `useFinancialProfile` hook's mutators).
 *   Right (1fr)  — Financial summary side card composed of FinSummaryTile
 *                  rows (Total assets / Total debt / Monthly payments /
 *                  Net worth). Values cap at body1 (16px) per §11b2 — the
 *                  old FinancialSummary used StatCard which can run larger
 *                  than the page-title h5.
 *
 * The Edit / Save / Cancel buttons live below the form (same pattern as the
 * legacy FinancialProfileSection) so the user can flip from read-only to
 * editable without leaving the tab.
 */

import AssetsIcon from '@mui/icons-material/AccountBalance';
import DebtIcon from '@mui/icons-material/CreditCard';
import PaymentIcon from '@mui/icons-material/Payment';
import NetWorthIcon from '@mui/icons-material/TrendingUp';
import { Box, GlassAlert, GlassButton, GlassCard, GlassChip, Typography, useI18n } from '@ops-matrix/design-system-ui';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useFinancialProfile } from '../../hooks/useFinancialProfile';
import AssetsForm from './AssetsForm';
import EmploymentInfoForm from './EmploymentInfoForm';
import FinSummaryTile from './FinSummaryTile';
import LiabilitiesForm from './LiabilitiesForm';

type FinSubTab = 'employment' | 'assets' | 'liabilities';

const SUB_TABS: Array<{ id: FinSubTab; labelKey: string }> = [
  { id: 'employment', labelKey: 'consumer.profile.financial.subTabEmployment' },
  { id: 'assets', labelKey: 'consumer.profile.financial.subTabAssets' },
  { id: 'liabilities', labelKey: 'consumer.profile.financial.subTabLiabilities' },
];

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function FinancialProfileTab(): ReactElement {
  const { t } = useI18n();
  const [subTab, setSubTab] = useState<FinSubTab>('employment');
  const {
    financialProfile,
    isEditing,
    isPending,
    isSaving,
    error,
    totals,
    startEditing,
    saveChanges,
    cancelChanges,
    updateEmployment,
    addAsset,
    updateAsset,
    removeAsset,
    addLiability,
    updateLiability,
    removeLiability,
  } = useFinancialProfile();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.5fr 1fr' },
        gap: 2.25,
        alignItems: 'start',
      }}
    >
      {/* ───── Left: sub-tabs + form ───── */}
      <Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {SUB_TABS.map((tab) => {
            const selected = subTab === tab.id;
            return (
              <GlassChip
                key={tab.id}
                label={t(tab.labelKey)}
                onClick={() => setSubTab(tab.id)}
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                // P5: meet portals.md 44px touch-target floor — these chips
                // are the only nav for the three financial sub-sections, not
                // an in-row secondary affordance.
                sx={{ minHeight: 44, px: 1.5 }}
              />
            );
          })}
        </Box>

        {error && (
          <GlassAlert severity="error" sx={{ mb: 2 }}>
            {t('financial.profile.failedToSave', {
              error: error instanceof Error ? error.message : 'Unknown error',
            })}
          </GlassAlert>
        )}

        <GlassCard sx={{ p: 2.5 }}>
          {subTab === 'employment' && (
            <EmploymentInfoForm
              employment={financialProfile.employment}
              onChange={updateEmployment}
              disabled={!isEditing}
            />
          )}
          {subTab === 'assets' && (
            <AssetsForm
              assets={financialProfile.assets}
              onAdd={addAsset}
              onUpdate={updateAsset}
              onRemove={removeAsset}
              disabled={!isEditing}
            />
          )}
          {subTab === 'liabilities' && (
            <LiabilitiesForm
              liabilities={financialProfile.liabilities}
              onAdd={addLiability}
              onUpdate={updateLiability}
              onRemove={removeLiability}
              disabled={!isEditing}
            />
          )}

          <Box sx={{ mt: 2.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {isEditing ? (
              <>
                <GlassButton variant="gradient" onClick={() => saveChanges()} disabled={isSaving}>
                  {isSaving ? t('common.saving') : t('common.saveChanges')}
                </GlassButton>
                <GlassButton variant="outlined" onClick={cancelChanges} disabled={isSaving}>
                  {t('common.cancel')}
                </GlassButton>
              </>
            ) : (
              <GlassButton variant="outlined" onClick={startEditing} disabled={isPending}>
                {t('common.edit')}
              </GlassButton>
            )}
          </Box>
        </GlassCard>
      </Box>

      {/* ───── Right: financial summary side card ───── */}
      <GlassCard sx={{ p: 2.25 }}>
        <Typography variant="overline" color="textSecondary" sx={{ display: 'block', mb: 1, letterSpacing: '0.08em' }}>
          {t('consumer.profile.financial.summaryHeading')}
        </Typography>
        <FinSummaryTile
          icon={<AssetsIcon fontSize="small" />}
          label={t('consumer.profile.financial.totalAssets')}
          value={formatCurrency(totals.totalAssets)}
          tone="positive"
          loading={isPending}
        />
        <FinSummaryTile
          icon={<DebtIcon fontSize="small" />}
          label={t('consumer.profile.financial.totalDebt')}
          value={formatCurrency(totals.totalDebt)}
          tone="negative"
          loading={isPending}
        />
        <FinSummaryTile
          icon={<PaymentIcon fontSize="small" />}
          label={t('consumer.profile.financial.monthlyPayments')}
          value={formatCurrency(totals.monthlyPayments)}
          loading={isPending}
        />
        <FinSummaryTile
          icon={<NetWorthIcon fontSize="small" />}
          label={t('consumer.profile.financial.netWorth')}
          value={formatCurrency(totals.netWorth)}
          tone={totals.netWorth >= 0 ? 'positive' : 'negative'}
          loading={isPending}
          isLast
        />
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1.5 }}>
          {t('consumer.profile.financial.netWorthFormula')}
        </Typography>
      </GlassCard>
    </Box>
  );
}

export default FinancialProfileTab;
